import './index.scss';

import React, { forwardRef, useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { ISearchPdfToolBarRef, ISearchPdfToolBarProps } from './interface';
import { Dropdown, Iconfont } from '@umu/umu-design';
import { $document } from '@umu/umu-dom';
import { useClickAway, useUpdateLayoutEffect } from 'ahooks';
import { isMobile } from '@umu/umu-ua';
import classNames from 'classnames';
import { PLACEMENT_ENUM } from '@umu/umu-design/es/components/Dropdown/interface';
import { SearchPdfToolBar } from './components/Search';
/**
 * SearchPdfToolDropdown 组件 Props
 */
interface ISearchPdfToolDropdownProps extends Omit<ISearchPdfToolBarProps, 'className'> {
    children?: React.ReactNode;
    iconType?: string;
    className?: string;
    overlayClassName?: string;
    // topLeft ｜ topCenter ｜ topRight ｜ bottomLeft ｜ bottomCenter ｜ bottomRight
    dropPlacement?: PLACEMENT_ENUM;
    iconClassName?: string;
    popupClassName?: string;
    // 是否点击触发元素打开搜索面板
    isClickTriggerOpen?: boolean;
    onBeforeShowSearchDrawer?: (showSearchDrawer: boolean) => void;
    /** 全屏状态：PC 端变化时自动关闭下拉面板，防止全屏切换后面板位置错位 */
    screenState?: string;
}

/**
 * SearchPdfToolDropdown 组件
 * 使用 Dropdown 包裹 SearchPdfToolBar，支持自定义触发元素
 * 如果 children 不存在，使用 Iconfont 作为触发元素，type 默认为 'Search'
 */
const SearchPdfToolDropdown = forwardRef<ISearchPdfToolBarRef, ISearchPdfToolDropdownProps>((props, ref) => {
    const {
        children,
        iconType = 'Search',
        dropPlacement = PLACEMENT_ENUM.BOTTOM_RIGHT,
        iconClassName = 'search-pdf-toolbar-dropdown-icon',
        overlayClassName = 'search-pdf-toolbar-dropdown',
        popupClassName,
        isClickTriggerOpen = true,
        onBeforeShowSearchDrawer,
        screenState,
        ...rest
    } = props;
    const [visible, setVisible] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // 全屏状态变化时关闭下拉面板：全屏切换会重构 DOM/定位上下文，
    // Dropdown 无法自动重定位，面板会错位到右侧被页面遮挡
    useUpdateLayoutEffect(() => {
        if (visible) {
            setVisible(false);
        }
    }, [screenState]);

    const handleTriggerClick = useCallback(
        (e?: React.MouseEvent<HTMLDivElement>) => {
            e?.stopPropagation?.();
            if (isClickTriggerOpen) {
                setVisible(!visible);
            }
        },
        [isClickTriggerOpen, visible],
    );
    const handleClose = useCallback(() => {
        handleTriggerClick();
        rest.onClose?.();
    }, [rest, handleTriggerClick]);

    const handleVisibleChange = useCallback((newVisible: boolean) => {
        if (isClickTriggerOpen) {
            return;
        }
        setVisible(newVisible);
    }, []);

    // 触发元素：如果存在 children 则使用 children，否则使用 Iconfont
    const triggerElement = useMemo(() => {
        if (children) {
            return children;
        }
        return <Iconfont type={iconType} className={`${iconClassName} ${visible ? 'active' : ''}`} />;
    }, [children, iconType, iconClassName, visible]);

    useEffect(() => {
        if (visible) {
            requestAnimationFrame(() => {
                if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
                    ref.current.onFocus?.();
                }
            });
        }
    }, [visible, ref]);
    useUpdateLayoutEffect(() => {
        onBeforeShowSearchDrawer?.(visible);
    }, [visible]);
    return (
        <div ref={containerRef} className="search-pdf-toolbar-dropdown-wrapper">
            <Dropdown
                trigger={['click']}
                visible={visible}
                placement={dropPlacement}
                overlayClassName={overlayClassName}
                onVisibleChange={handleVisibleChange}
                expandConfig={{
                    getPopupContainer: () => containerRef.current || $document.body,
                    // 禁止垂直方向自动翻转（adjustY: 0）：
                    // 工具栏吸顶后面板因可视区高度判断错误会向上弹出，
                    // 与 FlipModeMenu 保持一致，强制向下展开
                    placements: {
                        bottomRight: {
                            points: ['tr', 'br'],
                            overflow: { adjustX: 1, adjustY: 0 },
                            offset: [0, 4],
                            targetOffset: [0, 0],
                        },
                    },
                }}
                overlay={<SearchPdfToolBar ref={ref} {...rest} onClose={handleClose} />}
            >
                <div onClick={handleTriggerClick}>{triggerElement}</div>
            </Dropdown>
        </div>
    );
});

SearchPdfToolDropdown.displayName = 'SearchPdfToolDropdown';

const SearchPdfToolDrawer = forwardRef<ISearchPdfToolBarRef, ISearchPdfToolDropdownProps>((props, ref) => {
    const {
        children,
        iconType = 'Search',
        iconClassName = 'search-pdf-toolbar-drawer-trigger-icon',
        onBeforeShowSearchDrawer,
        ...rest
    } = props;
    const searchDrawerRef = useRef<HTMLDivElement>(null);
    const [isShowSearchDrawer, setIsShowSearchDrawer] = useState(false);
    const handleClose = useCallback(() => {
        setIsShowSearchDrawer(false);
        if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.onBlur?.();
        }
    }, []);
    const handleTriggerClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            // 使用 flushSync 让 React 在当前同步调用栈内完成状态更新与 DOM 提交，
            // 确保 input 脱离离屏位置（.hidden 移除）后，再调用 focus()。
            // iOS Safari 要求 focus() 必须在用户手势的同步调用栈中执行，
            // 否则虚拟键盘不会弹出（setTimeout / rAF / useEffect 均不满足）。
            flushSync(() => {
                setIsShowSearchDrawer(true);
            });
            if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
                ref.current.onFocus?.();
            }
        },
        [ref],
    );
    useClickAway(() => {
        if (isShowSearchDrawer && typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.onBlur?.();
        }
    }, searchDrawerRef);
    const triggerElement = useMemo(() => {
        if (children) {
            return children;
        }
        return <Iconfont type={iconType} className={`${iconClassName} ${isShowSearchDrawer ? 'active' : ''}`} />;
    }, [children, iconType, iconClassName, isShowSearchDrawer]);

    useUpdateLayoutEffect(() => {
        onBeforeShowSearchDrawer?.(isShowSearchDrawer);
    }, [isShowSearchDrawer]);

    return (
        <div className="search-pdf-toolbar-drawer" ref={searchDrawerRef}>
            <div
                className={classNames({
                    'search-pdf-toolbar-drawer-trigger': true,
                    'hidden': isShowSearchDrawer,
                })}
                onClick={handleTriggerClick}
            >
                {triggerElement}
            </div>
            <div
                className={classNames({
                    'search-pdf-toolbar-drawer-content': true,
                    'hidden': !isShowSearchDrawer,
                })}
            >
                <SearchPdfToolBar ref={ref} {...rest} onClose={handleClose} />
            </div>
        </div>
    );
});

SearchPdfToolDrawer.displayName = 'SearchPdfToolDrawer';

const SearchPdfTool = forwardRef<ISearchPdfToolBarRef, ISearchPdfToolDropdownProps>((props, ref) => {
    return isMobile ? <SearchPdfToolDrawer ref={ref} {...props} /> : <SearchPdfToolDropdown ref={ref} {...props} />;
});
SearchPdfTool.displayName = 'SearchPdfTool';

export { SearchPdfTool };
export type { ISearchPdfToolDropdownProps };
